import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DocumentsModal from './documentsModal/DocumentsModal';
import './Documentation.scss';
import { i18n } from 'core/i18n';
import { DocumentList } from './DocumentList';
import { DocumentationButton } from './DocumentationButton';
import { useDocumentsModal } from './hooks/useDocumentsModal';
import { updateDocumentsState } from './helper/updateDocumentsState.helper';
import { DocumentModalLoading } from './documentsModal/DocumentModalLoading';

const { formatText, FormattedText } = i18n
export const Documentation = ({
    docReady,
    initialDocumentsList,
    aDocumentWasNotAccepted,
    isLoading,
    precontractualLabel,
    saveDocumentList,
    onAcceptAllDocument,
    checkRadioButtonIneDir,
    onClickDocumentItem,
    onClickDocumentationButton,
}) => {
    const isMount = React.useRef(true);
    const [documentsList, setDocumentsList] = useState(initialDocumentsList.toJS());
    const [allDocumentRead, setCAllDocumentRead] = useState(false);
    const [loadingDocument, setLoadingDocument] = useState(false);
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

    const isAllDocumentsAccepted = useMemo(
        () => Array.isArray(documentsList) && documentsList.every(document => document.isAccepted),
        [documentsList]
    );
    const isAllDocumentsRead = useMemo(
        () => Array.isArray(documentsList) && documentsList.every(document => document.isRead),
        [documentsList]
    );
    useEffect(() => {
        setDocumentsList(initialDocumentsList.toJS());
    }, [initialDocumentsList]);

    const {
        showDocumentModal,
        documentsToOpen,
        documentToOpen,
        setDocumentsToOpen,
        handleCloseDocumentsModal,
        handleShowDocumentsModal,
        handleOpenDocumentsModal,
    } = useDocumentsModal({
        documentsList,
        isDocumentsLoaded: initialDocumentsList.size >= 2,
    });
    useEffect(() => {
        const documents = initialDocumentsList.toJS();
        const initialDocumentsListHasFile = documents.every(document => document.file);

        if (docReady && documentsToOpen.length === 0 && initialDocumentsListHasFile) {
            const mappedDocuments = documents.map((document, index) => ({
                ...document,
                fileName: formatText(`precontractualDocuments-${document.documentName}`),
                title: `${formatText(`precontractualDocuments-${document.documentName}`)} (${index + 1}/${documents.length})`,
            }))

            setDocumentsList(mappedDocuments);
            setLoadingDocument(false)
            !isMount.current && handleOpenDocumentsModal(mappedDocuments, documentToOpen);
        }

        isMount.current = false
    }, [docReady, documentsToOpen, initialDocumentsList])

    useEffect(() => {
        if (isLoading) {
            setLoadingDocument(true)
        }
    }, [isLoading])
    useEffect(() => {
        if (isAllDocumentsAccepted) {
            setCAllDocumentRead(true)
            onAcceptAllDocument()
        }
    }, [isAllDocumentsAccepted])

    const handleDocumentRead = documentReaded => {
        setDocumentsList(updateDocumentsState(documentReaded.dmsDocumentType, { isRead: true }));
        setDocumentsToOpen(updateDocumentsState(documentReaded.dmsDocumentType, { isRead: true }));
    };
    const updateDocumentList = (documentAccepted) => {
        setDocumentsList(updateDocumentsState(documentAccepted.dmsDocumentType, { isAccepted: true }));
        setDocumentsToOpen(updateDocumentsState(documentAccepted.dmsDocumentType, { isAccepted: true }));
        saveDocumentList(initialDocumentsList, documentAccepted.dmsDocumentType, { isAccepted: true })
        setLastScrollTop(0)

    };
    const updateRadioButtonDocumentList = (documentAccepted, check) => {
        setDocumentsList(updateDocumentsState(documentAccepted.dmsDocumentType, { radioButton: check }));
        setDocumentsToOpen(updateDocumentsState(documentAccepted.dmsDocumentType, { radioButton: check }));
        saveDocumentList(initialDocumentsList, documentAccepted.dmsDocumentType, { radioButton: check })

    };
    const handleDocumentAccepted = documentAccepted => {
        setDocumentsList(updateDocumentsState(documentAccepted.dmsDocumentType, { isAccepted: true }));
    };

    const handleClickDocumentItem = (document, documentName) => {
        if (onClickDocumentItem) {
            onClickDocumentItem(document, documentName);
        }
        handleShowDocumentsModal(document);
    };

    const handleClickDocumentationButton = () => {
        handleShowDocumentsModal();
        if (onClickDocumentationButton) {
            onClickDocumentationButton();
        }

    };

    return (
        <>
            <div className="documentation-docs-container">
                <div>
                    <p><FormattedText value="contractsView-contractsView_precontractualConditionsInformation2" /></p>
                    <DocumentList documentsList={documentsList} onClickDocumentItem={handleClickDocumentItem} currentDocumentIndex={currentDocumentIndex} />

                </div>
                {!allDocumentRead && (
                    <DocumentationButton documentsList={documentsList} onClick={() => handleClickDocumentationButton()} />
                )}
            </div>

            {allDocumentRead && (
                <div>
                    <p className="documentation-text">
                        {precontractualLabel}
                    </p>
                </div>
            )}

            <DocumentModalLoading isLoading={loadingDocument} />
            <DocumentsModal
                documentsList={allDocumentRead ? documentsList : documentsToOpen}
                showDocumentModal={showDocumentModal}
                handleClose={handleCloseDocumentsModal}
                onDocumentAccepted={handleDocumentAccepted}
                onSaveDocumentAcceptance={updateDocumentList}
                updateRadioButtonDocumentList={updateRadioButtonDocumentList}
                onDocumentRead={handleDocumentRead}
                aDocumentWasNotAccepted={aDocumentWasNotAccepted}
                checkRadioButtonIneDir={checkRadioButtonIneDir}
                allDocumentRead={allDocumentRead}
                setLastScrollTop={setLastScrollTop}
                lastScrollTop={lastScrollTop}
                currentDocumentIndex={currentDocumentIndex}
                setCurrentDocumentIndex={setCurrentDocumentIndex}
            />
        </>
    );
};

Documentation.propTypes = {
    docReady: PropTypes.bool,
    getDocuments: PropTypes.func,
    initialDocumentsList: PropTypes.array,
    onSaveDocumentAcceptance: PropTypes.func,
};
